import React, { useEffect, useState } from 'react';
import { Card, Grid, Box, Button, TableContainer, Tooltip, Stack } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import { makeStyles } from '@mui/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import EditIcon from '../../icons/editIcon.svg';
import DeleteIcon from '../../icons/deleteIcon.svg';
import BaseDialog from './components/baseDialog';
import DeleteDialog from './components/deleteDialog';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAssociatedPMs,
  selectAssociatedPMsDataPages,
  selectAssociatedPMsResponse,
  selectAssociatedPMState,
  selectCreateState,
  selectDeleteState,
  selectUpdateState,
} from './sagas/accountManagementSlice';
import TableHeader from '../shared/TableHeader';
import RegisteredIcon from '../../icons/RegisteredIcon.svg';
import NotRegisteredIcon from '../../icons/NotRegisteredIcon.svg';
import useRegInviteConfigs, { SubjectDetailsInvitation } from '../residents/hooks/useRegInviteConfigs';
import { SubjectDetailsPassReset, usePasswordResetConfigs } from '../residents/hooks/usePasswordResetConfigs';
import RegInviteModal from '../residents/InviteResident';
import ResetPasswordModal from '../residents/ResetPassword';
import SpinnerWithNoConfirmation from '../../shared/Modal/spinnerWithNoConfirmation';
import { CompleteCompanyStaffDto, PmSearchField, Role } from '@inosio/payments-sdk';
import { formatStringToSentenceCase } from '../../shared/util/formaters';

export type Partial<CompleteCompanyStaffDto> = {
  [el in keyof CompleteCompanyStaffDto]?: CompleteCompanyStaffDto[el];
};

export default function AccountManagement() {
  const defaultUser: Partial<CompleteCompanyStaffDto> = {};

  // manage dialog state
  const [openDelete, setOpenDelete] = useState(false);

  const [isUpsertModalOpen, setIsUpsertModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<CompleteCompanyStaffDto>();

  // manage user sessions

  const [currentUser, setCurrentUser] = useState(defaultUser);

  // this is necessary to enable the roll back option (back button) in the choose role dialog
  const [currentPage, setCurrentPage] = useState(0);
  const [rangeSize, setRangeSize] = useState(100);
  const [recordStartIndex, setRecordStartIndex] = useState(0);
  const [searchField, setSearchField] = React.useState('');
  const [searchValue, setSearchValue] = React.useState('');
  const [filteredRole, setFilteredRole] = React.useState('');
  const [isSearchValueEmpty, setIsSearchValueEmpty] = React.useState(false);
  const columnsSelectRef = React.createRef<HTMLSelectElement>();
  const roleSelectRef = React.createRef<HTMLSelectElement>();
  const [columnFilterSearchField, setColumnFilterSearchField] = React.useState('');
  const [searchChipOpen, setSearchChipOpen] = React.useState(false);
  const searchValueInputRef = React.createRef<HTMLInputElement>();
  const [searchBluePillValue, setSearchPillValue] = React.useState('');
  // load user accounts data from global store
  const associatePMsPages = useSelector(selectAssociatedPMsDataPages);
  const associatePmsResponse = useSelector(selectAssociatedPMsResponse);
  const associatePmState = useSelector(selectAssociatedPMState);
  const setSearchPageIndex = (pageNumber: number) => {
    if (!associatePMsPages[pageNumber]) {
      const newIndex = pageNumber * rangeSize;
      setRecordStartIndex(newIndex);
    }
  };
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPageNumber: number) => {
    setSearchPageIndex(newPageNumber);
    setCurrentPage(newPageNumber);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentPage(0);
    setRecordStartIndex(0);
    setRangeSize(parseInt(event.target.value));
  };
  const searchValueChangeHandler = (e: any) => {
    setSearchValue(e.target.value ? e.target.value.trim() : '');
  };
  const roleFilteredChangeHandler = (e: any) => {
    setRecordStartIndex(0);
    setCurrentPage(0);
    setFilteredRole(e.target.value);
    if (e.target.value === 'company_admin') {
      setSearchPillValue('Company Admin');
    }
    if (e.target.value === 'property_manager') {
      setSearchPillValue('Property Manager');
    }
    setSearchChipOpen(true);
  };

  const searchFieldChangeHandler = (e: any) => {
    setSearchField(e.target.value);
  };
  const handleChooseColumnSearchField = (event: any) => {
    event.preventDefault();
    if (columnsSelectRef.current) {
      setColumnFilterSearchField(columnsSelectRef?.current?.value);
    }
  };

  const optionValues = [
    { key: PmSearchField.Name, value: 'Name' },
    { key: PmSearchField.Role, value: 'Role' },
  ];

  const roleOptions = [
    { key: 'company_admin', value: 'Company Admin' },
    { key: 'property_manager', value: 'Property Manager' },
  ];

  const isDelete = useSelector(selectDeleteState);
  const isUpdate = useSelector(selectUpdateState);
  const isCreate = useSelector(selectCreateState);
  const dispatch = useDispatch();

  useEffect(() => {
    if (searchValueInputRef?.current?.value.length !== 0 && searchField !== 'Role') {
      dispatch(
        getAssociatedPMs({
          RangeSize: rangeSize,
          StartIndex: recordStartIndex,
          SearchField: searchField,
          SearchValue: searchValue,
        }),
      );
    } else if (filteredRole.length !== 0) {
      dispatch(
        getAssociatedPMs({
          RangeSize: rangeSize,
          StartIndex: recordStartIndex,
          SearchField: searchField,
          SearchValue: filteredRole,
        }),
      );
    } else {
      dispatch(
        getAssociatedPMs({
          RangeSize: rangeSize,
          StartIndex: recordStartIndex,
        }),
      );
    }
  }, [dispatch, rangeSize, recordStartIndex, isCreate, isUpdate, isDelete, filteredRole]);

  useEffect(() => {
    if (searchValue.length === 0) {
      setIsSearchValueEmpty(true);
    } else {
      setIsSearchValueEmpty(false);
    }
  }, [searchValue]);

  const resetTableRows = () => {
    setSearchChipOpen(false);
    setSearchValue('');
    setSearchField('');
    setSearchPillValue('');
    setRecordStartIndex(0);
    setCurrentPage(0);
    dispatch(
      getAssociatedPMs({
        RangeSize: rangeSize,
        StartIndex: 0,
      }),
    );
  };
  const searchUsers = (event: any) => {
    setCurrentPage(0);
    setRecordStartIndex(0);
    setSearchChipOpen(true);
    event.preventDefault();
    if (searchValueInputRef.current && searchValueInputRef?.current?.value.length !== 0) {
      setSearchPillValue(searchValueInputRef?.current?.value);
    }

    if (searchValueInputRef?.current?.value.length !== 0) {
      dispatch(
        getAssociatedPMs({
          RangeSize: rangeSize,
          StartIndex: recordStartIndex,
          SearchField: searchField,
          SearchValue: searchValue,
        }),
      );
    }
  };
  const useStyles = makeStyles({
    gridContainer: {
      background: '#FFFFFF',
      borderRadius: '24px',
    },
    dialogActionButtons: {
      '& .MuiButton-root': {
        background: '#39A2DB',
        marginBottom: 10,
        '&:hover': {
          background: '#226183',
        },
      },
    },
    textField: {},
    resizeTextField: {
      fontSize: 10,
    },
    dialogPaper: {
      height: 523,
      width: 472,
      fontSize: 10,
      '& .MuiButton-root': {
        borderRadius: 24,
      },
      '& .MuiDialogActions-root': { justifyContent: 'center' },
      '& .MuiTypography-root': { fontWeight: 800 },
    },
    deleteDialog: {
      '& .MuiDialogActions-root': { justifyContent: 'center' },
      '& .MuiDialogContent-root': { display: 'flex', flexDirection: 'column', textAlign: 'center', justifyContent: 'center', height: '100%' },
      '& .MuiButton-root': { borderRadius: 24 },
      '& .MuiDialogContentText-root': { marginTop: 12, marginBottom: 4, width: '100%' },
    },
    boldDialogHeader: {},
    divDialog: {
      display: 'flex',
    },
    divDialogButton: {
      display: 'flex',
      justifyContent: 'center',
    },
    createDialogBox: {
      '& .MuiDialogContent-root': {
        display: 'flex',
        justifyContent: 'center',
        alignContent: 'center',
      },
      [`& fieldset`]: {
        borderRadius: 24,
      },
    },
  });

  const classes = useStyles();
  const handleRowClick = (
    row: Partial<CompleteCompanyStaffDto>,
    setCurrentRow: React.Dispatch<React.SetStateAction<Partial<CompleteCompanyStaffDto>>>,
    flag: boolean,
    SetDialogState: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    setCurrentRow(row);
    SetDialogState(flag);
  };

  // const baseDialogConfig = {
  //   currentUser: currentUser,
  //   setCurrentUser: setCurrentUser,
  //   newUser: newUser,
  //   setNewUser: setNewUser,
  //   setOpenRoles: setOpenRoles,
  //   classes: classes,
  //   defaultUser: defaultUser,
  //   dispatch: dispatch,
  //   setOldRole: setOldRole,
  // };

  // const rolesDialogConfig = {
  //   classes: classes,
  //   openRoles: openRoles,
  //   setOpenRoles: setOpenRoles,
  //   currentUser: currentUser,
  //   setCurrentUser: setCurrentUser,
  //   newUser: newUser,
  //   setNewUser: setNewUser,
  //   oldRole: oldRole,
  //   setOldRole: setOldRole,
  // };

  const deleteConfig = {
    classes: classes,
    openDelete: openDelete,
    setOpenDelete: setOpenDelete,
    currentUser: currentUser,
    setCurrentUser: setCurrentUser,
    defaultUser: defaultUser,
    dispatch: dispatch,
  };

  /* 
  // start: password reset modal configs
  */
  const [openInvitationModal, setOpenInvitationModal] = React.useState(false);
  const [openResetPasswordModal, setOpenResetPasswordModal] = React.useState(false);

  const handleOpenInvitationModal = (pm: CompleteCompanyStaffDto) => {
    setCurrentUser(pm);
    if (!!pm?.UserUuid) {
      setOpenResetPasswordModal(true);
    } else {
      setOpenInvitationModal(true);
    }
  };

  const handleCloseModal = () => {
    setCurrentUser({});
    setOpenInvitationModal(false);
    setOpenResetPasswordModal(false);
  };

  const selectedPmDetailsInvitation: SubjectDetailsInvitation = {
    ProfileUuid: currentUser.Uuid ?? '',
    Email: currentUser.Email ?? '',
    FirstName: currentUser.FirstName ?? '',
    MiddleName: undefined, // pms don't have middlenames on record
    LastName: currentUser.LastName ?? '',
    RoleType: Role.PropertyManager,
  };

  const selectedPmDetailsPassReset: SubjectDetailsPassReset = {
    ProfileUuid: currentUser.Uuid ?? '',
    UserUuid: currentUser.UserUuid ?? '',
    Email: currentUser.Email ?? '',
    CreatedAt: currentUser.CreatedDate ?? '0',
  };

  const regInviteModalConfigs = useRegInviteConfigs({ handleCloseModal, subjectDetails: { ...selectedPmDetailsInvitation }, openModal: openInvitationModal });
  const passwordResetModalconfigs = usePasswordResetConfigs({ handleCloseModal, subjectDetails: { ...selectedPmDetailsPassReset }, openModal: openResetPasswordModal });

  /* 
  // end: password reset modal configs
  */

  const getRoleName = (typeId: number) => {
    switch (typeId) {
      case 1:
        return 'Company Admin';
      case 2:
        return 'Property Manager';
      case 3:
        return 'Maintenance Coordinator';
      case 4:
        return 'Maintenance Staff';
      default:
        return null;
    }
  };

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 'calc( 100vh - 144px )', pb: '64px' }} className={classes.gridContainer} style={{ position: 'relative' }}>
        <>
          <Box sx={{ display: 'flex', p: 1, pb: 2, justifyContent: 'space-between', alignItems: 'start', '& .MuiTextField-root': { m: 1, width: '25ch' } }}>
            <TableHeader
              searchValueChangeHandler={searchValueChangeHandler}
              searchValue={searchValue}
              isSearchValueEmpty={isSearchValueEmpty}
              searchFieldChangeHandler={searchFieldChangeHandler}
              searchField={searchField}
              optionValues={optionValues}
              handleSearch={searchUsers}
              columnsSelectRef={columnsSelectRef}
              handleChooseColumnSearchField={handleChooseColumnSearchField}
              resetTableRows={resetTableRows}
              setSearchChipOpen={setSearchChipOpen}
              searchChipOpen={searchChipOpen}
              resetSearchFilter={setSearchField}
              resetSearchValue={setSearchValue}
              columnFilterSearchField={columnFilterSearchField}
              searchBluePillValue={searchBluePillValue}
              searchValueInputRef={searchValueInputRef}
              rangeSize={rangeSize}
              recordStartIndex={recordStartIndex}
              hideCalendarIcon={true}
              hideDateRangeSearchPillChip={true}
              hideCalendarModal={true}
              hideSearchInput={true}
              roleOptions={roleOptions}
              roleSelectRef={roleSelectRef}
              roleFilteredChangeHandler={roleFilteredChangeHandler}
              filteredRole={filteredRole}
              resetFilterRole={setFilteredRole}
            />
            <Box>
              <Button
                variant="accountManagement"
                sx={{ height: 46, width: 152, fontSize: '15px', ml: 2, borderRadius: 24 }}
                onClick={() => {
                  setSelectedStaff(undefined);
                  setIsUpsertModalOpen(true);
                }}
              >
                Add New User
              </Button>
            </Box>
          </Box>
          {associatePmState.isFetchingAssociatePM ? (
            <Stack
              justifyContent="center"
              alignItems="center"
              style={{
                height: '1100px',
              }}
            >
              <SpinnerWithNoConfirmation />
            </Stack>
          ) : (
            <TableContainer style={{ flex: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="left" id="subTitle">
                      Name
                    </TableCell>
                    <TableCell align="left" id="subTitle">
                      Role
                    </TableCell>
                    <TableCell align="center" id="subTitle">
                      Registration
                    </TableCell>
                    <TableCell align="center" id="subTitle">
                      Edit
                    </TableCell>
                    <TableCell align="center" id="subTitle">
                      Remove
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {associatePMsPages?.[currentPage]?.Rows?.length ? (
                    associatePMsPages?.[currentPage]?.Rows?.map((pm) => (
                      <TableRow key={pm.Uuid} sx={{ '&:last-child td, &:last-child th': { border: 0 }, m: 0 }}>
                        <TableCell component="th" scope="row" id="content">{`${formatStringToSentenceCase(pm.FirstName)} ${formatStringToSentenceCase(pm.LastName)}`}</TableCell>
                        <TableCell align="left" id="content">
                          {getRoleName(pm.CompanyStaffTypeIds?.[0] || 0)}
                        </TableCell>
                        <TableCell align="center" id="content">
                          <Tooltip
                            title={!!pm.UserUuid ? 'Reset Password' : 'Invite to Portal'}
                            placement="right"
                            onClick={() => {
                              handleOpenInvitationModal(pm);
                            }}
                          >
                            <img
                              src={!!pm.UserUuid ? RegisteredIcon : NotRegisteredIcon}
                              alt=""
                              style={{ cursor: 'pointer' }}
                              className={!!pm.UserUuid ? 'registered' : 'not-registered'}
                              data-e2e={pm?.Uuid}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <img
                            src={EditIcon}
                            style={{ cursor: 'pointer', fontSize: '18px' }}
                            alt="Edit"
                            onClick={() => {
                              //set current user and set modal to open
                              setSelectedStaff(pm);
                              setIsUpsertModalOpen(true);
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <img
                            src={DeleteIcon}
                            style={{ cursor: 'pointer', fontSize: '18px' }}
                            alt="Delete"
                            onClick={() => {
                              handleRowClick(pm, setCurrentUser, true, setOpenDelete);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <></>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 50, 100, 200]}
            count={associatePmsResponse.TotalCount}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={rangeSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ position: 'absolute', bottom: 10, right: 10 }}
          />
          <Box id="dialogs">
            <BaseDialog classes={classes} EditUser={selectedStaff} isOpen={isUpsertModalOpen} setIsOpen={setIsUpsertModalOpen} />
            <DeleteDialog {...deleteConfig} />

            {openInvitationModal && Object.keys(currentUser).length !== 0 ? <RegInviteModal {...regInviteModalConfigs} /> : <></>}
            {openResetPasswordModal && Object.keys(currentUser).length !== 0 ? <ResetPasswordModal {...passwordResetModalconfigs} /> : <></>}
          </Box>
        </>
        {/* } */}
      </Card>
    </Grid>
  );
}
